import React, { useState } from 'react';
import apiClient from '../api';
import DatePicker from 'react-datepicker';
import "react-datepicker/dist/react-datepicker.css";
import { Chart as ChartJS, CategoryScale, LinearScale, BarElement, Title, Tooltip, Legend } from 'chart.js';
import { Bar } from 'react-chartjs-2';

ChartJS.register(CategoryScale, LinearScale, BarElement, Title, Tooltip, Legend);

function ReportingPage() {
  const [filters, setFilters] = useState({ offense_type: '', location: '' });
  const [period1, setPeriod1] = useState({ startDate: new Date(), endDate: new Date() });
  const [period2, setPeriod2] = useState({ startDate: new Date(), endDate: new Date() });
  const [reportType, setReportType] = useState('simple');
  const [reportData, setReportData] = useState(null);
  const [error, setError] = useState('');
  const [isLoading, setIsLoading] = useState(false);

  const handleFilterChange = (e) => {
    const { name, value } = e.target;
    setFilters(prev => ({ ...prev, [name]: value }));
  };

  const handleSubmit = async (e) => {
    e.preventDefault();
    setError('');
    setReportData(null);
    setIsLoading(true);

    const payload = {
      calculatePercentageChange: reportType === 'percentage',
      filters,
      period1,
      period2,
    };

    try {
      const response = await apiClient.post('/api/reports/custom', payload);
      setReportData(response.data);
    } catch (err) {
      setError('Failed to generate report.');
      console.error(err);
    } finally {
      setIsLoading(false);
    }
  };

  const chartData = reportData ? {
    labels: reportData.percentage_change !== undefined ? ['Period 1', 'Period 2'] : ['Total Count'],
    datasets: [{
      label: 'Offense Count',
      data: reportData.percentage_change !== undefined ? [reportData.period1_count, reportData.period2_count] : [reportData.total_count],
      backgroundColor: 'rgba(202, 138, 4, 0.6)',
      borderColor: 'rgba(202, 138, 4, 1)',
      borderWidth: 1,
    }],
  } : null;

  return (
    <div className="space-y-6">
      <h1 className="text-2xl font-bold text-slate-800">Custom Report Builder</h1>

      <form onSubmit={handleSubmit} className="card p-6">
        <div className="grid grid-cols-1 md:grid-cols-2 lg:grid-cols-3 gap-6">
          {/* Column 1 */}
          <div>
            <label className="block text-sm font-medium text-slate-700 mb-1">Report Type</label>
            <select value={reportType} onChange={(e) => setReportType(e.target.value)}>
              <option value="simple">Simple Count</option>
              <option value="percentage">Percentage Change</option>
            </select>
          </div>
          <div>
            <label className="block text-sm font-medium text-slate-700 mb-1">Filter by Offense Type</label>
            <input type="text" name="offense_type" placeholder="e.g., Theft" value={filters.offense_type} onChange={handleFilterChange} />
          </div>
          <div>
            <label className="block text-sm font-medium text-slate-700 mb-1">Filter by Location</label>
            <input type="text" name="location" placeholder="e.g., St. George's" value={filters.location} onChange={handleFilterChange} />
          </div>

          {/* Column 2 */}
          <div className="space-y-2">
            <label className="block text-sm font-medium text-slate-700">Period 1 Start Date</label>
            <DatePicker selected={period1.startDate} onChange={date => setPeriod1(p => ({ ...p, startDate: date }))} dateFormat="dd/MM/yyyy" className="w-full" />
          </div>
          <div className="space-y-2">
            <label className="block text-sm font-medium text-slate-700">Period 1 End Date</label>
            <DatePicker selected={period1.endDate} onChange={date => setPeriod1(p => ({ ...p, endDate: date }))} dateFormat="dd/MM/yyyy" className="w-full" />
          </div>
          <div></div> {/* Empty div for alignment */}

          {/* Column 3 (Conditional) */}
          {reportType === 'percentage' && (
            <>
              <div className="space-y-2">
                <label className="block text-sm font-medium text-slate-700">Period 2 Start Date</label>
                <DatePicker selected={period2.startDate} onChange={date => setPeriod2(p => ({ ...p, startDate: date }))} dateFormat="dd/MM/yyyy" className="w-full" />
              </div>
              <div className="space-y-2">
                <label className="block text-sm font-medium text-slate-700">Period 2 End Date</label>
                <DatePicker selected={period2.endDate} onChange={date => setPeriod2(p => ({ ...p, endDate: date }))} dateFormat="dd/MM/yyyy" className="w-full" />
              </div>
            </>
          )}
        </div>
        <div className="flex justify-end pt-4 mt-6 border-t border-slate-200">
          <button type="submit" disabled={isLoading} className="bg-gold text-white hover:bg-gold-dark font-bold py-2 px-4 rounded-lg shadow-sm disabled:opacity-50">
            {isLoading ? 'Generating...' : 'Generate Report'}
          </button>
        </div>
      </form>

      {/* Results Section */}
      <div className="card p-6">
        <h2 className="text-xl font-bold text-slate-800 border-b pb-2 mb-4">Report Results</h2>
        {isLoading && <p>Loading report...</p>}
        {error && <p className="text-red-500">{error}</p>}
        {reportData && (
          <div className="grid grid-cols-1 md:grid-cols-3 gap-6 items-start">
            <div className="md:col-span-1 space-y-2 text-slate-700">
              {reportData.total_count !== undefined && <p><strong>Total Count:</strong> {reportData.total_count}</p>}
              {reportData.percentage_change !== undefined && (
                <>
                  <p><strong>Period 1 Count:</strong> {reportData.period1_count}</p>
                  <p><strong>Period 2 Count:</strong> {reportData.period2_count}</p>
                  <p className="text-lg font-semibold"><strong>Percentage Change:</strong> {reportData.percentage_change}%</p>
                </>
              )}
            </div>
            <div className="md:col-span-2">
              <Bar data={chartData} />
            </div>
          </div>
        )}
        {!reportData && !isLoading && !error && <p>Generate a report to see results.</p>}
      </div>
    </div>
  );
}

export default ReportingPage;